import { setSentryAppContext, setSentryTenantContext, setSentryUserContext } from '@/plugins/sentry-context'
import httpClient from './http'
// Importar User dos tipos centralizados
import { User } from '@/types/users'
import * as Sentry from '@sentry/vue';

// Tipos para autenticação
export interface LoginCredentials {
  email: string
  password: string
  remember?: boolean
  captcha_token?: string
}

export interface AuthUser extends User {
  avatar?: string
}

interface ProfileResponse {
  user: AuthUser
  group?: AuthUser['group']
  sector?: AuthUser['sector']
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: AuthUser
}

export interface RefreshTokenResponse {
  token: string
  expires_in?: number
}

export interface RegisterData {
  name: string
  email: string
  password: string
  password_confirmation: string
}

export interface ForgotPasswordData {
  email: string
}

export interface ResetPasswordData {
  token: string
  email: string
  password: string
  password_confirmation: string
}

export interface UpdateProfileData {
  name?: string
  email?: string
  avatar?: File
  current_password?: string
  password?: string
  password_confirmation?: string
}

/**
 * Serviço de autenticação
 */
class AuthService {
  private readonly TOKEN_KEY = 'flowpilot_token'
  private readonly REFRESH_TOKEN_KEY = 'flowpilot_refresh_token'

  /**
   * Fazer login
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // Filtrar credenciais para enviar apenas o que o backend espera
      const cleanCredentials = {
        email: credentials.email,
        password: credentials.password
      }
      
      const response = await httpClient.post<LoginResponse>('/auth/login', cleanCredentials)
      
      setSentryAppContext()
      setSentryUserContext({
        id: response.user.id,
        email: response.user.email,
        username: response.user.name,
      })
      if (response.user.tenantId) {
        setSentryTenantContext({ id: response.user.tenantId })
      }

      // A API retorna os dados diretamente conforme documentação
      this.storeAuthData(response)
      return response
    } catch (error) {
      console.error('❌ Login failed:', error)
      Sentry.captureException(error, { tags: { feature: 'auth', action: 'login' } })
      throw error
    }
  }

  /**
   * Fazer logout
   */
  async logout(): Promise<void> {
    try {
      // Notificar o servidor sobre o logout conforme documentação da API
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY)
      if (refreshToken) {
        localStorage.removeItem(this.REFRESH_TOKEN_KEY)
        localStorage.removeItem(this.TOKEN_KEY)
        await httpClient.post('/auth/logout', { refreshToken })
      }
    } catch (error) {
      console.warn('⚠️ Logout request failed:', error)
      // Continuar com logout local mesmo se a API falhar
    } finally {
      setSentryUserContext(null)
      setSentryTenantContext(null)
      this.clearAuthData()
    }
  }

  /**
   * Fazer logout de todos os dispositivos
   */
  async logoutAll(): Promise<void> {
    try {
      await httpClient.post('/auth/logout-all')
    } catch (error) {
      console.warn('⚠️ Logout all request failed:', error)
      // Continuar com logout local mesmo se a API falhar
    } finally {
      setSentryUserContext(null)
      setSentryTenantContext(null)
      this.clearAuthData()
    }
  }

  /**
   * Registrar novo usuário - Não implementado na API atual
   */
  async register(_data: RegisterData): Promise<LoginResponse> {
    throw new Error('Registro não implementado na API atual')
  }

  /**
   * Atualizar token de acesso
   */
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY)
      if (!refreshToken) {
        throw new Error('Refresh token não encontrado')
      }

      const response = await httpClient.post<{ accessToken: string }>('/auth/refresh', {
        refreshToken: refreshToken
      })

      // A API retorna { accessToken: "token" }
      if (response && response.accessToken) {
        localStorage.setItem(this.TOKEN_KEY, response.accessToken)

        try {
          const payload = JSON.parse(atob(response.accessToken.split('.')[1]))
          if (payload.tenantId) {
            localStorage.setItem('tenant_id', payload.tenantId)
          }
        } catch {
          // Ignorar erro de decode — tenant será resolvido pelo host
        }

        return response.accessToken
      }

      throw new Error('Token não encontrado na resposta do refresh')
    } catch (error) {
      console.error('❌ Token refresh failed:', error)
      Sentry.captureException(error, { tags: { feature: 'auth', action: 'refresh_token' } })
      this.clearAuthData()
      throw error
    }
  }

  /**
   * Obter perfil do usuário atual
   */
  async getProfile(): Promise<AuthUser> {
    try {
      const response = await httpClient.get<AuthUser | ProfileResponse>('/auth/profile')
      return 'user' in response ? response.user : response
    } catch (error) {
      console.error('❌ Get profile failed:', error)
      throw error
    }
  }

  /**
   * Atualizar perfil do usuário - implementado conforme rota PATCH /auth/change-password
   */
  async updateProfile(data: UpdateProfileData): Promise<AuthUser> {
    try {
      // A API atual só suporta mudança de senha
      if (data.password && data.current_password) {
        await httpClient.patch('/auth/change-password', {
          currentPassword: data.current_password,
          newPassword: data.password
        })
      }

      // Retornar perfil atualizado
      return await this.getProfile()
    } catch (error) {
      console.error('❌ Update profile failed:', error)
      throw error
    }
  }

  /**
   * Solicitar recuperação de senha - Não implementado na API atual
   */
  async forgotPassword(_data: ForgotPasswordData): Promise<void> {
    throw new Error('Recuperação de senha não implementada na API atual')
  }

  /**
   * Redefinir senha - Não implementado na API atual
   */
  async resetPassword(_data: ResetPasswordData): Promise<void> {
    throw new Error('Reset de senha não implementado na API atual')
  }

  /**
   * Verificar se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY)
    return !!token
  }

  /**
   * Obter token de acesso atual
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * Obter dados do usuário atual
   * Agora retorna null - os dados devem ser obtidos via Pinia store
   */
  getCurrentUser(): AuthUser | null {
    // Dados do usuário agora são gerenciados pelo Pinia
    // Use a store auth para acessar os dados do usuário
    return null
  }

  /**
   * Armazenar dados de autenticação
   * Agora salva apenas tokens no localStorage
   */
  private storeAuthData(data: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, data.accessToken)
    
    if (data.refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, data.refreshToken)
    }

    if (data.user?.tenantId) {
      localStorage.setItem('tenant_id', data.user.tenantId)
    }
    
    // Dados do usuário serão gerenciados pelo Pinia store
    // Retornar os dados para que a store possa utilizá-los
  }

  /**
   * Limpar dados de autenticação
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.REFRESH_TOKEN_KEY)
    // Dados do usuário serão limpos pelo Pinia store
  }

  /**
   * Verificar e atualizar token se necessário
   */
  async checkAndRefreshToken(): Promise<boolean> {
    try {
      const token = this.getToken()
      if (!token) return false

      // Tentar obter o perfil para verificar se o token é válido
      await this.getProfile()
      return true
    } catch (error) {
      // Se falhar, tentar atualizar o token
      try {
        await this.refreshToken()
        return true
      } catch (refreshError) {
        console.warn('⚠️ Token refresh failed, user needs to login again')
        return false
      }
    }
  }
}

// Exportar instância singleton
export const authService = new AuthService()
export default authService